/**
 * New-UX counterpart of components/ButtonWithPromise: a NewUxButton whose onClick returns
 * a promise. While that promise is in flight the button is disabled, its icon/children are
 * swapped for a spinner plus the `pending` label, and further clicks are ignored.
 */
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import useIsMounted from '../../../../../../hooks/useIsMounted';
import { NewUxButton } from './Button';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
  flex-shrink: 0;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export type NewUxButtonWithPromiseProps = Omit<
  React.ComponentProps<typeof NewUxButton>,
  'onClick'
> & {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => Promise<unknown>;
  pending: React.ReactNode;
};

export const NewUxButtonWithPromise = React.forwardRef<
  HTMLButtonElement,
  NewUxButtonWithPromiseProps
>(({ onClick, pending, disabled, startIcon, children, ...rest }, ref) => {
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setLoading(true);
    const done = () => {
      if (isMounted.current) setLoading(false);
    };
    return onClick(e).then(done, done);
  };

  return (
    <NewUxButton
      ref={ref}
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading}
      onClick={handleClick}
      startIcon={loading ? undefined : startIcon}
    >
      {loading ? (
        <>
          <Spinner size={16} /> {pending}
        </>
      ) : (
        children
      )}
    </NewUxButton>
  );
});
